import { AiCarouselItem, ChatSession } from '@/features/AiChat/model/api/chat/types.ts';
import { ChatService } from '@/features/AiChat/model/context/types.ts';
import { getAiCarouselApi } from '@/features/AiChat/model/api/chat';
import { useAiAccess } from '@/features/AiChat/model/services/use-ai-access';
import { useAuthStore } from '@/features/Auth/model/authStore';
import { useEffect, useState } from 'react';
import { useSessionService } from '@/features/AiChat/model/services/use-session-service.ts';

export const useChatService = (): ChatService => {
  const aiAccess = useAiAccess();
  const sessionGeneration = useAuthStore((state) => state.sessionGeneration);
  const scopeKey = `${sessionGeneration}:${aiAccess.tokenData?.user_id ?? ''}:${aiAccess.tokenData?.org_id ?? ''}`;
  const [aiCarouselItems, setAiCarouselItems] = useState<AiCarouselItem[]>([]);
  const [chatIsOpen, setChatIsOpen] = useState<boolean>(false);
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const sessionService = useSessionService({
    canUseAi: aiAccess.canUseAi,
    scopeKey
  });
  const {
    setActiveSession,
    setInputValue,
    setSelectedFiles,
    stopPolling
  } = sessionService.methods;

  useEffect(() => {
    stopPolling();
    setActiveSession(null);
    setChatSessions([]);
    setInputValue('');
    setSelectedFiles([]);
  }, [aiAccess.canUseAi, scopeKey, setActiveSession, setInputValue, setSelectedFiles, stopPolling]);

  useEffect(() => {
    let isCurrentScope = true;
    setAiCarouselItems([]);
    if (aiAccess.canUseAi) {
      getAiCarouselApi().then((items) => {
        if (isCurrentScope) setAiCarouselItems(items);
      }).catch(console.log);
    }
    return () => {
      isCurrentScope = false;
    };
  }, [aiAccess.canUseAi, scopeKey]);

  const closeChatSession = () => {
    stopPolling();
    setActiveSession(null);
    setSelectedFiles([]);
    setInputValue('');
  };

  const closeChat = () => {
    setChatIsOpen(false);
    closeChatSession();
  };

  const openChat = () => {
    setChatIsOpen(true);
  };

  const removeSession = (id: number) => {
    setChatSessions((prev) => {
      return prev.filter((session: ChatSession) => session.id !== id);
    });
  };

  const addSession = (session: ChatSession) => {
    setChatSessions((prev) => {
      return prev.concat(session);
    });
  };

  const openChatSession = (session: ChatSession) => {
    if (!aiAccess.canUseAi) return;

    setActiveSession(session);
  };

  return {
    methods: {
      addSession,
      closeChat,
      closeChatSession,
      openChat,
      openChatSession,
      removeSession,
      setChatSessions
    },
    state: {
      aiAccess,
      aiCarouselItems,
      chatIsOpen,
      chatSessions,
      sessionService
    }
  };
};